/** Presentation helpers shared by the table, the gallery and the exports. */

export const EM_DASH = "—";

export function formatDate(value) {
  if (!value) return EM_DASH;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

/** Sortable key for a date-ish string; unknown dates sort last. */
export function dateSortKey(value) {
  const time = new Date(value || "").getTime();
  return Number.isNaN(time) ? -Infinity : time;
}

export function formatDuration(ms) {
  if (!ms && ms !== 0) return "";
  return ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`;
}

export function formatList(values) {
  return values?.length ? values.join(", ") : EM_DASH;
}

/** "IC 009: computer software; IC 042: SaaS" style summary for a row's goods & services. */
export function formatGoodsAndServices(list, { maxChars } = {}) {
  if (!list?.length) return "";
  const text = list
    .filter((g) => g.description)
    .map((g) =>
      g.classNumber ? `IC ${g.classNumber}: ${g.description}` : g.description,
    )
    .join("  ");
  if (!maxChars || text.length <= maxChars) return text;
  return `${text.slice(0, maxChars).trimEnd()}…`;
}

/** Applicant / owner text for a row, falling back to the full record's owners. */
export function formatOwners(row) {
  if (row?.applicantName) return row.applicantName;
  const owners = (row?.detail?.owners || []).map((o) => o.name).filter(Boolean);
  return owners.join(", ");
}

/** Registration number: the register's own, or — for an EU mark — the application number once registered. */
export function registrationNumberOf(row) {
  const own = row?.detail?.registrationNumber;
  if (own) return own;
  return row?.source === "euipo" && row?.registrationDate
    ? row.applicationNumber
    : "";
}

/**
 * Register status vocabulary.
 *
 * Offices use different words for the same thing (EUIPO says "Registered",
 * some national offices say "Ended" or "Expired"). We map everything onto
 * four states so a colour always means the same thing to the reader.
 */
const STATUS_GROUPS = [
  {
    tone: "live",
    words: ["registered", "protected", "granted", "live", "accepted"],
  },
  {
    tone: "pending",
    words: [
      "filed",
      "pending",
      "application published",
      "under examination",
      "published",
    ],
  },
  {
    tone: "opposed",
    words: ["opposed", "opposition", "refused", "rejected", "cancelled"],
  },
  {
    tone: "dead",
    words: [
      "expired",
      "ended",
      "withdrawn",
      "surrendered",
      "lapsed",
      "invalid",
    ],
  },
];

/**
 * Register statuses often arrive as ALL_CAPS_WITH_UNDERSCORES
 * ("APPLICATION_PUBLISHED"). Show them as words; leave anything already
 * written as prose alone.
 */
export function humanize(value) {
  if (!value) return "";
  const text = String(value).replace(/_/g, " ").trim();
  if (text !== text.toUpperCase()) return text;
  const lower = text.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export const prettyStatus = humanize;

export function statusTone(status) {
  const value = (status || "").toLowerCase().replace(/_/g, " ");
  if (!value) return "dead";
  const match = STATUS_GROUPS.find((group) =>
    group.words.some((w) => value.includes(w)),
  );
  return match ? match.tone : "dead";
}

export const STATUS_TONE_CLASS = {
  live: "bg-live-soft text-live border-live/25",
  pending: "bg-pending-soft text-pending border-pending/25",
  opposed: "bg-opposed-soft text-opposed border-opposed/25",
  dead: "bg-dead-soft text-dead border-dead/25",
};

export function similarityTone(score) {
  if (score === undefined || score === null) return "none";
  if (score >= 75) return "opposed";
  if (score >= 40) return "pending";
  return "live";
}

export const SIMILARITY_TEXT_CLASS = {
  opposed: "text-opposed",
  pending: "text-pending",
  live: "text-live",
  none: "text-ink-300",
};

/**
 * Deterministic tile colour for marks with no figurative element, so the
 * same mark always looks the same and neighbouring rows stay distinct.
 */
const TILE_PALETTE = [
  { bg: "#111827", fg: "#A78BFA" },
  { bg: "#1E293B", fg: "#F1F5F9" },
  { bg: "#7C3AED", fg: "#FFFFFF" },
  { bg: "#334155", fg: "#C4B5FD" },
  { bg: "#0F7A52", fg: "#E3F4ED" },
  { bg: "#64748B", fg: "#F9FAFB" },
  { bg: "#4F46E5", fg: "#EDE9FE" },
  { bg: "#6D28D9", fg: "#FFFFFF" },
];

export function tilePalette(text) {
  const source = text || "?";
  let hash = 0;
  for (let i = 0; i < source.length; i += 1) {
    hash = (hash << 5) - hash + source.charCodeAt(i);
    hash |= 0;
  }
  return TILE_PALETTE[Math.abs(hash) % TILE_PALETTE.length];
}

export function initials(wordMark) {
  if (!wordMark) return EM_DASH;
  const parts = wordMark.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return EM_DASH;
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/** Office code to a readable name; unknown codes pass through unchanged. */
export const OFFICE_NAMES = {
  AD: "Andorra",
  AE: "United Arab Emirates",
  AF: "Afghanistan",
  AG: "Antigua & Barbuda",
  AI: "Anguilla",
  AL: "Albania",
  AM: "Armenia",
  AO: "Angola",
  AQ: "Antarctica",
  AR: "Argentina",
  AS: "American Samoa",
  AT: "Austria",
  AU: "Australia",
  AW: "Aruba",
  AX: "Åland Islands",
  AZ: "Azerbaijan",
  BA: "Bosnia & Herzegovina",
  BB: "Barbados",
  BD: "Bangladesh",
  BE: "Belgium",
  BF: "Burkina Faso",
  BG: "Bulgaria",
  BH: "Bahrain",
  BI: "Burundi",
  BJ: "Benin",
  BL: "St. Barthélemy",
  BM: "Bermuda",
  BN: "Brunei",
  BO: "Bolivia",
  BQ: "Caribbean Netherlands",
  BR: "Brazil",
  BS: "Bahamas",
  BT: "Bhutan",
  BV: "Bouvet Island",
  BW: "Botswana",
  BY: "Belarus",
  BZ: "Belize",
  CA: "Canada",
  CC: "Cocos (Keeling) Islands",
  CD: "Congo - Kinshasa",
  CF: "Central African Republic",
  CG: "Congo - Brazzaville",
  CH: "Switzerland",
  CI: "Côte d’Ivoire",
  CK: "Cook Islands",
  CL: "Chile",
  CM: "Cameroon",
  CN: "China",
  CO: "Colombia",
  CR: "Costa Rica",
  CU: "Cuba",
  CV: "Cape Verde",
  CW: "Curaçao",
  CX: "Christmas Island",
  CY: "Cyprus",
  CZ: "Czechia",
  DE: "Germany",
  DJ: "Djibouti",
  DK: "Denmark",
  DM: "Dominica",
  DO: "Dominican Republic",
  DY: "Benin",
  DZ: "Algeria",
  EA: "Eurasian Patent Organization",
  EC: "Ecuador",
  EE: "Estonia",
  EG: "Egypt",
  EH: "Western Sahara",
  ER: "Eritrea",
  ES: "Spain",
  ET: "Ethiopia",
  EU: "European Union",
  FI: "Finland",
  FJ: "Fiji",
  FK: "Falkland Islands",
  FM: "Micronesia",
  FO: "Faroe Islands",
  FR: "France",
  GA: "Gabon",
  GB: "United Kingdom",
  GD: "Grenada",
  GE: "Georgia",
  GF: "French Guiana",
  GG: "Guernsey",
  GH: "Ghana",
  GI: "Gibraltar",
  GL: "Greenland",
  GM: "Gambia",
  GN: "Guinea",
  GP: "Guadeloupe",
  GQ: "Equatorial Guinea",
  GR: "Greece",
  GS: "South Georgia & South Sandwich Islands",
  GT: "Guatemala",
  GU: "Guam",
  GW: "Guinea-Bissau",
  GY: "Guyana",
  HK: "Hong Kong SAR China",
  HM: "Heard & McDonald Islands",
  HN: "Honduras",
  HR: "Croatia",
  HT: "Haiti",
  HU: "Hungary",
  HV: "Burkina Faso",
  ID: "Indonesia",
  IE: "Ireland",
  IL: "Israel",
  IM: "Isle of Man",
  IN: "India",
  IO: "British Indian Ocean Territory",
  IQ: "Iraq",
  IR: "Iran",
  IS: "Iceland",
  IT: "Italy",
  JE: "Jersey",
  JM: "Jamaica",
  JO: "Jordan",
  JP: "Japan",
  KE: "Kenya",
  KG: "Kyrgyzstan",
  KH: "Cambodia",
  KI: "Kiribati",
  KM: "Comoros",
  KN: "St. Kitts & Nevis",
  KP: "North Korea",
  KR: "South Korea",
  KW: "Kuwait",
  KY: "Cayman Islands",
  KZ: "Kazakhstan",
  LA: "Laos",
  LB: "Lebanon",
  LC: "St. Lucia",
  LI: "Liechtenstein",
  LK: "Sri Lanka",
  LR: "Liberia",
  LS: "Lesotho",
  LT: "Lithuania",
  LU: "Luxembourg",
  LV: "Latvia",
  LY: "Libya",
  MA: "Morocco",
  MC: "Monaco",
  MD: "Moldova",
  ME: "Montenegro",
  MF: "St. Martin",
  MG: "Madagascar",
  MH: "Marshall Islands",
  MK: "North Macedonia",
  ML: "Mali",
  MM: "Myanmar (Burma)",
  MN: "Mongolia",
  MO: "Macao SAR China",
  MP: "Northern Mariana Islands",
  MQ: "Martinique",
  MR: "Mauritania",
  MS: "Montserrat",
  MT: "Malta",
  MU: "Mauritius",
  MV: "Maldives",
  MW: "Malawi",
  MX: "Mexico",
  MY: "Malaysia",
  MZ: "Mozambique",
  NA: "Namibia",
  NC: "New Caledonia",
  NE: "Niger",
  NF: "Norfolk Island",
  NG: "Nigeria",
  NH: "Vanuatu",
  NI: "Nicaragua",
  NL: "Netherlands",
  NO: "Norway",
  NP: "Nepal",
  NR: "Nauru",
  NU: "Niue",
  NZ: "New Zealand",
  OM: "Oman",
  PA: "Panama",
  PE: "Peru",
  PF: "French Polynesia",
  PG: "Papua New Guinea",
  PH: "Philippines",
  PK: "Pakistan",
  PL: "Poland",
  PM: "St. Pierre & Miquelon",
  PN: "Pitcairn Islands",
  PR: "Puerto Rico",
  PS: "Palestinian Territories",
  PT: "Portugal",
  PW: "Palau",
  PY: "Paraguay",
  QA: "Qatar",
  RE: "Réunion",
  RH: "Zimbabwe",
  RO: "Romania",
  RS: "Serbia",
  RU: "Russia",
  RW: "Rwanda",
  SA: "Saudi Arabia",
  SB: "Solomon Islands",
  SC: "Seychelles",
  SD: "Sudan",
  SE: "Sweden",
  SG: "Singapore",
  SH: "St. Helena",
  SI: "Slovenia",
  SJ: "Svalbard & Jan Mayen",
  SK: "Slovakia",
  SL: "Sierra Leone",
  SM: "San Marino",
  SN: "Senegal",
  SO: "Somalia",
  SR: "Suriname",
  SS: "South Sudan",
  ST: "São Tomé & Príncipe",
  SV: "El Salvador",
  SX: "Sint Maarten",
  SY: "Syria",
  SZ: "Eswatini",
  TC: "Turks & Caicos Islands",
  TD: "Chad",
  TF: "French Southern Territories",
  TG: "Togo",
  TH: "Thailand",
  TJ: "Tajikistan",
  TK: "Tokelau",
  TL: "Timor-Leste",
  TM: "Turkmenistan",
  TN: "Tunisia",
  TO: "Tonga",
  TR: "Türkiye",
  TT: "Trinidad & Tobago",
  TV: "Tuvalu",
  TW: "Taiwan",
  TZ: "Tanzania",
  UA: "Ukraine",
  UG: "Uganda",
  UK: "United Kingdom",
  UM: "U.S. Outlying Islands",
  US: "United States",
  UY: "Uruguay",
  UZ: "Uzbekistan",
  VA: "Vatican City",
  VC: "St. Vincent & Grenadines",
  VD: "Vietnam",
  VE: "Venezuela",
  VG: "British Virgin Islands",
  VI: "U.S. Virgin Islands",
  VN: "Vietnam",
  VU: "Vanuatu",
  WF: "Wallis & Futuna",
  WS: "Samoa",
  XK: "Kosovo",
  YD: "Yemen",
  YE: "Yemen",
  YT: "Mayotte",
  ZA: "South Africa",
  ZM: "Zambia",
  ZW: "Zimbabwe",
  EM: "European Union",
  WO: "WIPO (international)",
  BX: "Benelux",
  AP: "ARIPO (African Regional IP Org.)",
  OA: "OAPI (African IP Organization)",
  GC: "GCC Patent Office",
};

export function officeLabel(code) {
  if (!code) return EM_DASH;
  return OFFICE_NAMES[code] ? `${OFFICE_NAMES[code]} (${code})` : code;
}
